/* ============================================================================
   room-state.ts — the canonical room state model.

   Source of truth: OCST/KN/26-RSM Room Status Transition Matrix Rev 01.

   A room's condition is THREE ORTHOGONAL DIMENSIONS, not one status field.
   Flattening them into a single enum is the mistake this module exists to
   prevent: it is why a room can be simultaneously occupied, clean and out of
   service, and why "ready" cannot be stored.

     housekeeping  what the attendant has done            DIRTY -> READY
     occupancy     what the PMS says about the guest      VACANT / OCCUPIED / ...
     service       whether the room may be sold at all    IN_SERVICE / OOS / OOO

   READINESS IS DERIVED. It is a function of the three, computed on read. There
   is no `ready` column and there must never be one — a stored flag drifts from
   the facts that produced it, and the first support call is always "the board
   says ready but the room is out of order".
   ========================================================================= */

export const HOUSEKEEPING_STATES = ['DIRTY', 'IN_PROGRESS', 'CLEAN', 'INSPECTED'] as const
export type HousekeepingState = (typeof HOUSEKEEPING_STATES)[number]

export const OCCUPANCY_STATES = ['VACANT', 'OCCUPIED', 'ARRIVING', 'DEPARTING'] as const
export type OccupancyState = (typeof OCCUPANCY_STATES)[number]

export const SERVICE_STATES = ['IN_SERVICE', 'OUT_OF_SERVICE', 'OUT_OF_ORDER'] as const
export type ServiceState = (typeof SERVICE_STATES)[number]

export interface RoomCondition {
  housekeeping: HousekeepingState
  occupancy: OccupancyState
  service: ServiceState
}

/* ---- derived readiness ---------------------------------------------------
   A room is sellable only when all three agree. The reason is returned with
   the answer because every screen that shows "not ready" is immediately asked
   why, and recomputing the reason at the call site is how the explanations
   drift apart between the board, the list and the report. */

export type NotReadyReason =
  | 'OUT_OF_ORDER'
  | 'OUT_OF_SERVICE'
  | 'AWAITING_CLEAN'
  | 'BEING_CLEANED'
  | 'AWAITING_INSPECTION'
  | 'STILL_OCCUPIED'

export interface Readiness {
  ready: boolean
  reason: NotReadyReason | null
}

/**
 * Whether inspection is required before a room counts as ready.
 * Configurable per property (CQ-20 makes permissions and workflow configurable);
 * the default is true because that is how the client's supervisors work today.
 */
export interface ReadinessPolicy {
  requireInspection: boolean
}

export const DEFAULT_READINESS_POLICY: ReadinessPolicy = { requireInspection: true }

/**
 * Whether a guest is physically in the room right now.
 *
 * EVERY member is listed, and DEPARTING is the one that matters: this rule used to
 * test `occupancy === 'OCCUPIED'` and let everything else fall through to the
 * housekeeping state, so an inspected room whose guest was still in it and due out at
 * eleven reported READY TO SELL. Nothing failed; the board simply offered an occupied
 * room to the front desk.
 *
 *   OCCUPIED   guest in the room
 *   DEPARTING  guest in the room, due out today — the PMS moves the room to VACANT at
 *              check-out, so this state still means occupied
 *   VACANT     empty
 *   ARRIVING   empty, guest due in — this is the state a room most needs to be ready
 *              IN, so it must not be treated as occupied
 *
 * The C# domain holds the same rule, and the two are held together by a truth table
 * generated from the domain and asserted here — see readinessContract.test.ts.
 */
export function guestIsPresent(occupancy: OccupancyState): boolean {
  switch (occupancy) {
    case 'OCCUPIED':  return true
    case 'DEPARTING': return true
    case 'VACANT':    return false
    case 'ARRIVING':  return false
  }
}

export function readiness(
  c: RoomCondition,
  policy: ReadinessPolicy = DEFAULT_READINESS_POLICY,
): Readiness {
  // Service availability is checked first and beats everything. An out-of-order
  // room is not ready however spotless it is.
  if (c.service === 'OUT_OF_ORDER') return { ready: false, reason: 'OUT_OF_ORDER' }
  if (c.service === 'OUT_OF_SERVICE') return { ready: false, reason: 'OUT_OF_SERVICE' }

  // Then occupancy. A room with a guest still in it is not ready for the next
  // one, whatever the housekeeping state says.
  if (guestIsPresent(c.occupancy)) return { ready: false, reason: 'STILL_OCCUPIED' }

  switch (c.housekeeping) {
    case 'DIRTY':       return { ready: false, reason: 'AWAITING_CLEAN' }
    case 'IN_PROGRESS': return { ready: false, reason: 'BEING_CLEANED' }
    case 'CLEAN':
      return policy.requireInspection
        ? { ready: false, reason: 'AWAITING_INSPECTION' }
        : { ready: true, reason: null }
    case 'INSPECTED':   return { ready: true, reason: null }
  }
}

export const isReady = (c: RoomCondition, p?: ReadinessPolicy) => readiness(c, p).ready

/* ---- the visual channel each dimension is allowed to use -----------------
   Encoded here rather than in the component so that a future screen cannot
   quietly invent a fourth channel or reuse a taken one. Brand blue is
   deliberately absent from every state: it is reserved for chrome and action,
   so a blue thing on the board is always something you can click. */

export type RoomVisualTone =
  | 'dirty' | 'prog' | 'clean' | 'ready'   // housekeeping -> hue
  | 'oos'   | 'ooo'                         // service      -> hue + dashed border

/**
 * The tone that drives the tile's hue and border.
 * Service availability overrides the housekeeping hue, because a room you
 * cannot sell is the more urgent fact — but the housekeeping state is still
 * shown in the tile's label, so nothing is hidden.
 */
export function roomTone(c: RoomCondition): RoomVisualTone {
  if (c.service === 'OUT_OF_ORDER') return 'ooo'
  if (c.service === 'OUT_OF_SERVICE') return 'oos'
  switch (c.housekeeping) {
    case 'DIRTY':       return 'dirty'
    case 'IN_PROGRESS': return 'prog'
    case 'CLEAN':       return 'clean'
    case 'INSPECTED':   return 'ready'
  }
}

/* ---- permitted transitions ------------------------------------------------
   THE TRANSITION MATRIX, T1–T9, from OCST/KN/26-RSM Rev 01.

   **DEFECT #119, AND THIS COMMENT IS WHERE IT LIVED.** What stood here said the table was
   "enforced here so the same rule governs the web board, the handset and the API client,
   rather than three copies of it" — a claim that it prevented drift, written above a copy
   that had drifted. It was missing THREE of the nine rules:

       CLEAN     -> IN_PROGRESS   T4, a rejected inspection
       DIRTY     -> CLEAN         T8, the override
       INSPECTED -> IN_PROGRESS   T9, a touch-up

   and two of the six it did have were annotated with the wrong transition's name: it
   called CLEAN -> DIRTY "inspection rejected", which is T4 and goes to IN_PROGRESS, and
   IN_PROGRESS -> DIRTY "the attendant abandons the task", which is T7 and is a supervisor
   cancelling work — an act of authority that costs a typed reason.

   IT HAD NEVER PUT A ROOM IN THE WRONG STATE, for the single reason that makes a contract
   the right fix rather than a correction: **nothing called it.** Both functions were
   exported and used by no screen for thirty-three sprints, so the table was wrong and
   harmless, waiting for the first caller.

   **SO IT IS NOW HELD IN STEP BY A GENERATED FILE.** `TransitionContractTests` writes
   `app/contracts/room-transitions.json` from the C# domain and `roomTransitionContract
   .test.ts` asserts everything below against it, cell by cell over the whole cross
   product. This is the same arrangement readiness has had since Sprint 12, and for the
   same reason: the handset needs the answer with no server to ask, so a second
   implementation is a cost we accept and then police.

   **WHY THE TABLE IS STILL WRITTEN OUT HERE** rather than importing the JSON: this is
   shipped code and that file is a test artefact. A build that read its rules from
   `app/contracts` would put the contract in the bundle and, worse, would make the two
   suites agree by construction — which is the one thing a cross-language contract must
   not do. The duplication is the point; the test is what makes it safe. */

const HK_TRANSITIONS: Record<HousekeepingState, readonly HousekeepingState[]> = {
  DIRTY:       ['IN_PROGRESS', 'CLEAN'],                // T1 start · T8 override
  IN_PROGRESS: ['CLEAN', 'DIRTY'],                      // T2 submit · T7 supervisor cancels
  CLEAN:       ['INSPECTED', 'IN_PROGRESS', 'DIRTY'],   // T3 accept · T4 reject · T6 re-soiled
  INSPECTED:   ['DIRTY', 'IN_PROGRESS'],                // T5 soiled · T9 touch-up
}

/* THE THREE THAT COST SOMEBODY A SENTENCE, and the client has to know which they are —
   a form that demands a reason for all nine nags on the six that are ordinary work, and
   one that demands it for none is refused by the server on these three after the
   supervisor has finished typing everything else. */
const REQUIRES_REASON: readonly `${HousekeepingState}->${HousekeepingState}`[] = [
  'CLEAN->IN_PROGRESS',   // T4 — a supervisor rejecting somebody's work
  'IN_PROGRESS->DIRTY',   // T7 — a supervisor cancelling work in progress
  'DIRTY->CLEAN',         // T8 — the override, deliberately expensive
]

export function canTransition(from: HousekeepingState, to: HousekeepingState): boolean {
  /* A ROOM IS NEVER MOVED TO WHERE IT ALREADY IS. The domain answers this case
     separately — "the room is already CLEAN" rather than "that is not a permitted
     transition" — and a client that let it through would send a no-op to be refused. */
  if (from === to) return false
  return HK_TRANSITIONS[from].includes(to)
}

export function nextStates(from: HousekeepingState): readonly HousekeepingState[] {
  return HK_TRANSITIONS[from]
}

/** Whether this move demands a typed reason. False for a move that is not permitted at
 *  all — there is nothing to give a reason for. */
export function transitionNeedsReason(
  from: HousekeepingState, to: HousekeepingState,
): boolean {
  return canTransition(from, to) && REQUIRES_REASON.includes(`${from}->${to}`)
}
